import React, { useState, useEffect } from 'react';
import { X, FileUp, Trash2, CalendarDays } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { parseISO, isValid } from 'date-fns';
import type { Post } from '../../types/calendar';
import 'react-datepicker/dist/react-datepicker.css';

interface IdeaDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  idea: Post | null;
  onPromoteToDraft: (idea: Post) => void;
  onDelete: (ideaId: string) => void;
  onUpdate?: (ideaId: string, data: { title: string; description: string; reminderAt: string | null }) => void;
  isPromoting?: boolean;
  isDeleting?: boolean;
  isUpdating?: boolean;
}

export const IdeaDetailModal: React.FC<IdeaDetailModalProps> = ({
  isOpen,
  onClose,
  idea,
  onPromoteToDraft,
  onDelete,
  onUpdate,
  isPromoting = false,
  isDeleting = false,
  isUpdating = false
}) => {
  const { t } = useTranslation();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [reminderDate, setReminderDate] = useState<Date | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (idea && isOpen) {
      setTitle(idea.title || idea.content || '');
      setDescription(idea.description || '');
      if (idea.reminderAt) {
        const d = parseISO(idea.reminderAt);
        setReminderDate(isValid(d) ? d : null);
      } else {
        setReminderDate(null);
      }
      setHasChanges(false);
    }
  }, [idea, isOpen]);

  const handleFieldChange = (setter: (v: any) => void) => (value: any) => {
    setter(value);
    setHasChanges(true);
  };

  const handleSaveChanges = () => {
    if (!idea || !onUpdate) return;
    onUpdate(idea.id, {
      title: title.trim(),
      description: description.trim(),
      reminderAt: reminderDate ? reminderDate.toISOString() : null
    });
  };

  if (!isOpen || !idea) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />

        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 border border-black">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <span className="text-xl">💡</span>
              <h2 className="text-xl font-semibold">
                {t('ideaDetail.title', 'Post Idea')}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label htmlFor="idea-detail-title" className="block text-sm font-medium text-gray-700 mb-1">
                {t('ideaDetail.ideaTitle', 'Title')}
              </label>
              <input
                id="idea-detail-title"
                type="text"
                value={title}
                onChange={(e) => handleFieldChange(setTitle)(e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="idea-detail-desc" className="block text-sm font-medium text-gray-700 mb-1">
                {t('ideaDetail.description', 'Notes')}
              </label>
              <textarea
                id="idea-detail-desc"
                value={description}
                onChange={(e) => handleFieldChange(setDescription)(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
              />
            </div>

            {/* Brand (read-only) */}
            {idea.brandName && (
              <div className="flex items-center gap-2">
                {idea.brandLogo && (
                  <img
                    src={idea.brandLogo}
                    alt={idea.brandName}
                    className="w-5 h-5 rounded object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                )}
                <span className="text-sm text-gray-500">{idea.brandName}</span>
              </div>
            )}

            {/* Reminder date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <CalendarDays className="inline h-4 w-4 mr-1 text-amber-500" />
                {t('ideaModal.reminderDate', 'Remind me on')}
              </label>
              <DatePicker
                selected={reminderDate}
                onChange={(date: Date | null) => handleFieldChange(setReminderDate)(date)}
                dateFormat="PPP"
                minDate={new Date()}
                placeholderText={t('ideaModal.reminderPlaceholder', 'Pick a date (optional)')}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
                isClearable
                inline
              />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex flex-col gap-2">
            {hasChanges && onUpdate && (
              <button
                type="button"
                onClick={handleSaveChanges}
                disabled={!title.trim() || isUpdating}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-transparent shadow-sm px-4 py-2.5 bg-amber-500 text-base font-medium text-white hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 sm:text-sm disabled:opacity-50"
              >
                {isUpdating
                  ? t('common.saving', 'Saving...')
                  : t('common.save', 'Save')}
              </button>
            )}

            <button
              type="button"
              onClick={() => onPromoteToDraft(idea)}
              disabled={isPromoting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-transparent shadow-sm px-4 py-2.5 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm disabled:opacity-50"
            >
              <FileUp className="h-4 w-4" />
              {isPromoting
                ? t('common.saving', 'Saving...')
                : t('ideaDetail.promoteToDraft', 'Turn into a post')}
            </button>

            <button
              type="button"
              onClick={() => onDelete(idea.id)}
              disabled={isDeleting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-red-300 shadow-sm px-4 py-2.5 bg-white text-base font-medium text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
              {isDeleting
                ? t('common.deleting', 'Deleting...')
                : t('common.delete', 'Delete')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
